import {
    BaseType,
    BuildComponentType,
    CardinalDirection, ColorEnum,
    ElevatorType, HazardousState, HazardType, HeritageProtectionLevel, LevelType,
    RiskConstructionType, RoofSafetyStatus, RoofShape, RoomType,
    UtilityCategory, OutdoorCategory, WindowPaneType, ParkingAreaKind, ParkingSpotType, StorageUnitType,
    TenantKind, CoTenantRole, LeaseObjectType, UnitKind, UnitTenure
} from "./Enums";
import { BuildingType } from "./BuildingTypeEnums";
import { DoorCategory, AnchorPointSafetyStatus } from "./protocols";
import { WorkOrderPriority, WorkOrderStatus } from "./workOrders";
import { WORK_ORDER_LABELS } from "../Pages/WorkOrders/workOrderSections";
import { IssuePriority } from "./issues";
import { InspectionCategory, RegulatoryType } from "../Pages/BuildingChecks/types";

export const translateOther = (key: string | undefined): string => {
    if (key === undefined) {
        return 'Odefinierat'
    }

    const translations: { [key: string]: string } = {
        roof: "Tak",
        facade: "Fasad",
        foundation: "Grund",
        drainage: "Takavvattning",
        chimney: "Skorsten/murstock",

        asbestos: "Asbest",
        led: "Bly",
        pcb: "PCB",
        arsenic: "Arsenik",
        blueConcrete: "Blåbetong",
        radon: "Radon",

        SUSPECTED: "Misstänkt",
        CONFIRMED: "Bekräftat",
    };

    return translations[key] || key;
};

// One table keyed by stored value: two enums sharing a value would overwrite each other
// (EnumTranslations.test.ts guards this). An enum that collides gets its own translator below.
type TranslateableValue = UtilityCategory | OutdoorCategory | CardinalDirection | BuildComponentType | RiskConstructionType |
    ElevatorType | ColorEnum | HazardType | RoofSafetyStatus | RoofShape | RoomType | HazardousState | WindowPaneType | LevelType |
    BaseType | BuildingType | HeritageProtectionLevel | DoorCategory | AnchorPointSafetyStatus |
    ParkingSpotType | TenantKind | CoTenantRole |
    UnitKind | UnitTenure
    ;

type ExhaustiveCheck<T extends string | number> = {
    [K in T]: string;
};

export function translate(value: TranslateableValue | undefined | string): string {
    if (value === undefined) {
        return "Odefinierat"
    }

    const translations: ExhaustiveCheck<TranslateableValue> = {
        // Material translations are now handled via MaterialsContext.getMaterialTranslation()

        [UtilityCategory.Ventilation]: "Ventilation",
        [UtilityCategory.HeatingCooling]: "Värme/kyla",
        [UtilityCategory.Elevator]: "Hiss",
        [UtilityCategory.FireSafety]: "Brandskydd",
        [UtilityCategory.MechanicalMaintenanceRiskConstructions]: "Mekaniskt underhåll",
        [UtilityCategory.Electrical]: "El",
        [UtilityCategory.WaterAndSewage]: "Vatten & Avlopp",
        [UtilityCategory.DoorAutomation]: "Dörrautomatik",
        [UtilityCategory.Appliances]: "Vitvaror",
        [UtilityCategory.UtilityOther]: "Övrigt",  // Uses "utility_other" to avoid collision with BuildingType.other
        // Former utility categories, moved to the base type Fast inredning (fixed_furnishing) 2026-10.
        // Kept so change-log entries from before the move still render.
        sanitary_equipment: "Sanitetsutrustning",
        fixed_furnishings: "Fast inredning",

        [OutdoorCategory.GroundSurface]: "Markyta",
        [OutdoorCategory.PlayEquipment]: "Lekutrustning",
        [OutdoorCategory.Boundaries]: "Avgränsningar",
        [OutdoorCategory.Structures]: "Konstruktioner",
        [OutdoorCategory.PermanentFurniture]: "Permanenta möbler",
        [OutdoorCategory.OutdoorOther]: "Övrigt",

        [CardinalDirection.North]: "Nord",
        [CardinalDirection.NorthEast]: "Nordöst",
        [CardinalDirection.East]: "Öst",
        [CardinalDirection.SouthEast]: "Sydöst",
        [CardinalDirection.South]: "Syd",
        [CardinalDirection.SouthWest]: "Sydväst",
        [CardinalDirection.West]: "Väst",
        [CardinalDirection.NorthWest]: "Nordväst",

        // BuildComponentType translations are now handled via BuildComponentTypesContext



        [RiskConstructionType.RiskCrawlSpace]: "Krypgrund",
        [RiskConstructionType.RiskSingleStageSealedRender]: "Enstegstätad putsfasad (utan luftspalt)",
        [RiskConstructionType.RiskFloatingFloor]: "Flytande golv",
        [RiskConstructionType.RiskUninsulatedSlabOnGround]: "Oisolerad platta på mark",
        [RiskConstructionType.RiskExternallyVentilatedAttic]: "Uteluftsventilerad vind",
        [RiskConstructionType.RiskCompactRoofNoAirGap]: "Kompakttak (utan luftspalt)",
        [RiskConstructionType.RiskInternalDrainage]: "Invändig avvattning",
        [RiskConstructionType.RiskLowSlopeRoof]: "Låglutande tak",
        [RiskConstructionType.RiskBuiltInWaterConnections]: "Dolda/inbyggda vattenkopplingar",
        [RiskConstructionType.RiskExternalWallNoVaporBarrier]: "Yttervägg utan diffusionsspärr",
        [RiskConstructionType.RiskUngroundedElectricalSystem]: "Ojordat elsystem",
        [RiskConstructionType.RiskCablesThroughExternalWallRoof]: "Kabelgenomföringar genom yttervägg och yttertak",
        [RiskConstructionType.RiskRaisedFloorOnSlab]: "Uppreglat golv på betongplatta",
        [RiskConstructionType.RiskTilesOnGypsumInWetRoom]: "Kakel på gipsskiva i våtrum",

        [ElevatorType.TractionElevator]: "Linhiss",
        [ElevatorType.Stairlift]: "Trapphiss",
        [ElevatorType.HydraulicElevator]: "Hydraulisk hiss",

        [ColorEnum.ColorWhite]: "Vit",
        [ColorEnum.ColorBlack]: "Svart",
        [ColorEnum.ColorLightGrey]: "Ljusgrå",
        [ColorEnum.ColorDarkGrey]: "Mörkgrå",
        [ColorEnum.ColorBeige]: "Beige",
        [ColorEnum.ColorYellow]: "Gul",
        [ColorEnum.ColorLightBlue]: "Ljusblå",
        [ColorEnum.ColorDarkBlue]: "Mörkblå",
        [ColorEnum.ColorLightGreen]: "Ljusgrön",
        [ColorEnum.ColorDarkGreen]: "Mörkgrön",
        [ColorEnum.ColorRed]: "Röd",
        [ColorEnum.ColorFaluRed]: "Faluröd",
        [ColorEnum.ColorBrown]: "Brun",
        [ColorEnum.ColorTerracotta]: "Terrakotta",
        [ColorEnum.ColorPink]: "Rosa",
        [ColorEnum.ColorPurple]: "Lila",
        [ColorEnum.ColorOrange]: "Orange",
        [ColorEnum.ColorTurquoise]: "Turkos",

        [HazardType.HazardAsbestos]: "Asbest",
        [HazardType.HazardLed]: "Bly",
        [HazardType.HazardPcb]: "PCB",
        [HazardType.HazardArsenic]: "Arsenik",
        [HazardType.HazardBlueConcrete]: "Blåbetong",
        [HazardType.HazardRadon]: "Radon",

        [RoofShape.RoofShapeGable]: "Sadeltak",
        [RoofShape.RoofShapeMansard]: "Mansardtak",
        [RoofShape.RoofShapeHip]: "Valmat tak",
        [RoofShape.RoofShapeShed]: "Pulpettak",
        [RoofShape.RoofShapeInvertedShed]: "Motfallstak",
        [RoofShape.RoofShapeCylindrical]: "Cylindriskt tak",
        [RoofShape.RoofShapeLowSlope]: "Flackt tak",
        [RoofShape.RoofShapeFlat]: "Platt tak",

        [RoofSafetyStatus.APPROVED]: "Godkänd",
        [RoofSafetyStatus.NEEDS_INSPECTION]: "Behöver besiktning",
        [RoofSafetyStatus.FAILED]: "Underkänd",
        [RoofSafetyStatus.UNKNOWN]: "Okänd",

        [AnchorPointSafetyStatus.SAFE]: "Säker",
        [AnchorPointSafetyStatus.NOT_SAFE]: "Ej säker",
        [AnchorPointSafetyStatus.UNDETERMINED]: "Ej bedömd",

        [RoomType.RoomGeneral]: "Allmänt",
        [RoomType.RoomKitchen]: "Kök",
        [RoomType.RoomAssembly]: "Matsal/samlingshall",
        [RoomType.RoomOffice]: "Kontor",
        [RoomType.RoomBathroom]: "Badrum/toalett",
        [RoomType.RoomStorage]: "Förråd/förvaring",
        [RoomType.RoomUtility]: "Teknikrum",
        [RoomType.RoomEntryway]: "Hall",
        [RoomType.RoomHallway]: "Korridor",
        [RoomType.RoomChanging]: "Omklädningsrum",
        [RoomType.RoomWork]: "Arbetsrum",
        [RoomType.RoomConference]: "Konferens",
        [RoomType.RoomLecture]: "Föreläsning",
        [RoomType.RoomExhibit]: "Utställning",
        [RoomType.RoomGenericAttic]: "Vind",
        [RoomType.RoomWarmAttic]: "Varmvind",
        [RoomType.RoomColdAttic]: "Kallvind",
        [RoomType.RoomCompactRoof]: "Kompakttak",
        [RoomType.RoomLaundry]: "Tvättstuga",
        [RoomType.RoomStairwell]: "Trapphus",
        [RoomType.RoomElevatorShaft]: "Hisschakt",

        [RoomType.RoomBedroom]: "Sovrum",
        [RoomType.RoomLivingRoom]: "Vardagsrum",
        [RoomType.RoomDiningRoom]: "Matsal",
        [RoomType.RoomServer]: "Serverrum",
        [RoomType.RoomCleaning]: "Städrum",
        [RoomType.RoomTrash]: "Soprum",
        [RoomType.RoomBikeStorage]: "Cykelförråd",
        [RoomType.RoomReception]: "Reception",
        [RoomType.RoomCafeteria]: "Cafeteria",
        [RoomType.RoomGym]: "Gym",
        [RoomType.RoomArchive]: "Arkiv",
        [RoomType.RoomGarage]: "Garage",
        [RoomType.RoomMedical]: "Behandlingsrum",
        [RoomType.RoomLibrary]: "Bibliotek",
        [RoomType.RoomWorkshop]: "Verkstad",
        [RoomType.RoomWc]: "WC",
        [RoomType.RoomShower]: "Duschrum",
        [RoomType.RoomApartment]: "Lägenhet",
        [RoomType.RoomCommercial]: "Lokal",

        [ParkingSpotType.Car]: "Bilplats",
        [ParkingSpotType.Motorcycle]: "MC-plats",
        [ParkingSpotType.Accessible]: "Handikapplats",
        [ParkingSpotType.Oversized]: "Stor plats",

        [TenantKind.Person]: "Privatperson",
        [TenantKind.Company]: "Företag",

        [CoTenantRole.ContractHolder]: "Medhyresgäst",
        [CoTenantRole.Resident]: "Medboende",

        [UnitKind.Apartment]: "Lägenhet",
        [UnitKind.Commercial]: "Lokal",

        [UnitTenure.Bostadsratt]: "Bostadsrätt",
        [UnitTenure.Hyresratt]: "Hyresrätt",
        [UnitTenure.Own]: "Egen användning",

        [HazardousState.Suspected]: "Misstänkt",
        [HazardousState.Confirmed]: "Bekräftad",

        [WindowPaneType.OnePane]: "Enkelglas",
        [WindowPaneType.TwoPane]: "2-glas",
        [WindowPaneType.ThreePane]: "3-glas",

        [DoorCategory.interior_door]: "Innerdörr",
        [DoorCategory.exterior_door]: "Ytterdörr",

        [LevelType.Basement]: "Källare",
        [LevelType.GroundFloor]: "Bottenvåning",
        [LevelType.NthFloor]: "Våning",
        [LevelType.Attic]: "Vind",

        [BaseType.Ceiling]: "Innertak",
        [BaseType.Chimney]: "Skorsten/murstock",
        [BaseType.RoofDrainage]: "Takavvattning",
        [BaseType.GroundDrainage]: "Dränering/dagvatten",
        [BaseType.Facade]: "Fasad",
        [BaseType.Floor]: "Golv",
        [BaseType.Foundation]: "Grund",
        [BaseType.GeoTechnics]: "Geoteknik",
        [BaseType.Outdoor]: "Utomhusobjekt",
        [BaseType.Roof]: "Tak",
        [BaseType.RoofSafety]: "Taksäkerhet",
        [BaseType.Structural]: "Bärande konstruktion",
        [BaseType.Wall]: "Vägg",
        [BaseType.Window]: "Fönster",
        [BaseType.Utility]: "Fastighetsteknik",
        [BaseType.Door]: "Dörr",
        [BaseType.SheetMetal]: "Kompletterande plåtarbeten",
        [BaseType.Membrane]: "Tätskikt",
        [BaseType.LockMechanism]: "Lås",
        [BaseType.Riser]: "Stammar",
        [BaseType.Balcony]: "Balkonger",
        [BaseType.Miscellaneous]: "Övrigt",
        [BaseType.FixedFurnishing]: "Fast inredning",

        [BuildingType.standard]: "Byggnad",
        [BuildingType.complement]: "Komplementsbyggnad",
        [BuildingType.toiletArchipelago]: "Skärgårdstoalett",
        [BuildingType.toiletReserve]: "Reservatstoalett",
        [BuildingType.other]: "Annat byggnadsverk",

        [HeritageProtectionLevel.Byggnadsminne]: "Byggnadsminne",
        [HeritageProtectionLevel.KMarkt]: "K-märkt byggnad",
        [HeritageProtectionLevel.QMarkt]: "Q-märkt byggnad",
        [HeritageProtectionLevel.KulturhistorisktVardefull]: "Kulturhistoriskt värdefull byggnad",
        [HeritageProtectionLevel.Riksintresse]: "Riksintresse för kulturmiljövård",
        [HeritageProtectionLevel.Kulturreservat]: "Kulturreservat",

    } as const;

    const translationKey = value as keyof typeof translations;
    return translations[translationKey] ?? translationKey;
}

/**
 * Label for a component without a cardinal direction. No direction is a valid
 * choice (the component is treated the same on every side), not a missing value.
 */
export function allDirectionsLabel(baseType: BaseType | string | null | undefined): string {
    return baseType === BaseType.Facade ? "Samtliga fasadsidor" : "Samtliga väderstreck";
}

// Own tables: "outdoor", "door" and "other" are also BaseType/BuildingType values in translate().
const parkingAreaKindTranslations: Record<ParkingAreaKind, string> = {
    [ParkingAreaKind.Garage]: "Garage",
    [ParkingAreaKind.Outdoor]: "Utomhusparkering",
    [ParkingAreaKind.Carport]: "Carport",
};

const storageUnitTypeTranslations: Record<StorageUnitType, string> = {
    [StorageUnitType.Mesh]: "Gallerförråd",
    [StorageUnitType.Board]: "Plankförråd",
    [StorageUnitType.Door]: "Förråd med dörr",
    [StorageUnitType.Room]: "Enskilt rum",
};

const leaseObjectTypeTranslations: Record<LeaseObjectType, string> = {
    [LeaseObjectType.ParkingSpot]: "Parkeringsplats",
    [LeaseObjectType.StorageUnit]: "Förråd",
    [LeaseObjectType.Apartment]: "Lägenhet",
    [LeaseObjectType.CommercialUnit]: "Lokal",
    [LeaseObjectType.Other]: "Annat",
};

export function translateParkingAreaKind(kind: ParkingAreaKind): string {
    return parkingAreaKindTranslations[kind] ?? kind;
}

export function translateStorageUnitType(type: StorageUnitType): string {
    return storageUnitTypeTranslations[type] ?? type;
}

export function translateLeaseObjectType(type: LeaseObjectType): string {
    return leaseObjectTypeTranslations[type] ?? type;
}

/**
 * Translations for work order priority levels
 */
const workOrderPriorityTranslations: Record<WorkOrderPriority, string> = {
    [WorkOrderPriority.LOW]: 'Låg',
    [WorkOrderPriority.MEDIUM]: 'Medium',
    [WorkOrderPriority.HIGH]: 'Hög',
};

/**
 * Translations for work order statuses
 */
const workOrderStatusTranslations: Record<WorkOrderStatus, string> = {
    [WorkOrderStatus.OPEN]: WORK_ORDER_LABELS.statusOpen,
    [WorkOrderStatus.COMPLETED]: WORK_ORDER_LABELS.statusCompleted,
    [WorkOrderStatus.CANCELLED]: WORK_ORDER_LABELS.statusCancelled,
};

/**
 * Get Swedish translation for work order priority
 */
export function translateWorkOrderPriority(priority: WorkOrderPriority): string {
    return workOrderPriorityTranslations[priority] ?? priority;
}

/**
 * Get Swedish translation for work order status
 */
export function translateWorkOrderStatus(status: WorkOrderStatus): string {
    return workOrderStatusTranslations[status] ?? status;
}

/**
 * Translations for issue priority levels
 */
const issuePriorityTranslations: Record<IssuePriority, string> = {
    [IssuePriority.LOW]: 'Låg',
    [IssuePriority.MEDIUM]: 'Normal',
    [IssuePriority.HIGH]: 'Hög',
};

/**
 * Get Swedish translation for issue priority
 */
export function translateIssuePriority(priority: IssuePriority): string {
    return issuePriorityTranslations[priority] ?? priority;
}

/**
 * Translations for inspection categories (Kontroller)
 */
const inspectionCategoryTranslations: Record<InspectionCategory, string> = {
    [InspectionCategory.MeterReadings]: 'Avläsningar',
    [InspectionCategory.RoutineOperations]: 'Löpande drift',
    [InspectionCategory.Service]: 'Service',
    [InspectionCategory.ConditionAssessment]: 'Skickbedömning',
};

/**
 * Get Swedish translation for inspection category
 */
export function translateInspectionCategory(category: InspectionCategory): string {
    return inspectionCategoryTranslations[category] ?? category;
}

/**
 * Translations for regulatory types (Myndighetskrav)
 */
const regulatoryTypeTranslations: Record<RegulatoryType, string> = {
    [RegulatoryType.Fire]: 'Brand',
    [RegulatoryType.Ventilation]: 'OVK',
    [RegulatoryType.Elevator]: 'Hiss',
    [RegulatoryType.DrinkingWater]: 'Dricksvatten',
    [RegulatoryType.RoofSafety]: 'Taksäkerhet',
    [RegulatoryType.Other]: 'Annat',
};

/**
 * Get Swedish translation for regulatory type
 */
export function translateRegulatoryType(type: RegulatoryType): string {
    return regulatoryTypeTranslations[type] ?? type;
}
